import { useCallback, useEffect, useState, type ReactNode } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import type { EmblaOptionsType } from "embla-carousel";
import type { CarouselConfig } from "@site-factory/config";
import { cn } from "../utils";

export interface CarouselProps extends CarouselConfig {
  children: ReactNode;
  className?: string;
  /** Optional custom aria-label */
  ariaLabel?: string;
}

/**
 * Converts responsive breakpoints to number of slides for current viewport.
 */
function getSlidesToShow(
  config: CarouselConfig["slidesToShow"]
): number {
  if (typeof config === "number") return config;
  if (!config) return 1;

  // Simple client-side breakpoint detection
  if (typeof window === "undefined") return config.desktop || config.tablet || config.mobile || 1;

  const width = window.innerWidth;
  if (width >= 1024) return config.desktop || config.tablet || config.mobile || 1;
  if (width >= 768) return config.tablet || config.mobile || 1;
  return config.mobile || 1;
}

/**
 * True carousel component with auto-play, navigation, and touch gestures.
 * Built on Embla Carousel for performance and flexibility.
 *
 * @example
 * ```tsx
 * <Carousel autoplay interval={3000} showArrows showDots>
 *   <div>Slide 1</div>
 *   <div>Slide 2</div>
 *   <div>Slide 3</div>
 * </Carousel>
 * ```
 */
export function Carousel({
  children,
  className,
  ariaLabel = "Carousel",
  autoplay = false,
  interval = 3000,
  showArrows = true,
  showDots = true,
  loop = true,
  slidesToShow = 1,
  gap = "gap-4",
  align = "start",
  duration = 300,
  pauseOnHover = true,
  pauseOnInteraction = true,
}: CarouselProps) {
  const slides = getSlidesToShow(slidesToShow);

  // Setup Embla with options
  const options: EmblaOptionsType = {
    loop,
    align,
    slidesToScroll: 1,
    duration,
    skipSnaps: false,
  };

  // Setup autoplay plugin
  const plugins = autoplay
    ? [
        Autoplay({
          delay: interval,
          stopOnInteraction: pauseOnInteraction,
          stopOnMouseEnter: pauseOnHover,
        }),
      ]
    : [];

  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // Update state on scroll
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  // Initialize scroll snaps
  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Navigation functions
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  // Keyboard navigation
  useEffect(() => {
    if (!emblaApi) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    };

    const viewport = emblaApi.rootNode();
    viewport.addEventListener("keydown", handleKeyDown);

    return () => {
      viewport.removeEventListener("keydown", handleKeyDown);
    };
  }, [emblaApi, scrollPrev, scrollNext]);

  // Convert children to array
  const childArray = Array.isArray(children) ? children : [children];

  return (
    <div className={cn("relative", className)} aria-label={ariaLabel} aria-roledescription="carousel">
      {/* Viewport */}
      <div className="overflow-hidden" ref={emblaRef} tabIndex={0}>
        <div className={cn("flex", gap)}>
          {childArray.map((child, index) => (
            <div
              key={index}
              className="flex-shrink-0"
              style={{
                flex: `0 0 ${100 / slides}%`,
                minWidth: 0,
              }}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${childArray.length}`}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      {showArrows && childArray.length > slides && (
        <>
          <button
            type="button"
            onClick={scrollPrev}
            disabled={!canScrollPrev && !loop}
            className={cn(
              "absolute left-2 top-1/2 -translate-y-1/2 z-10",
              "h-10 w-10 rounded-full bg-white/90 shadow-lg",
              "flex items-center justify-center",
              "hover:bg-white transition-colors",
              "disabled:opacity-40 disabled:cursor-not-allowed",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            )}
            aria-label="Previous slide"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>

          <button
            type="button"
            onClick={scrollNext}
            disabled={!canScrollNext && !loop}
            className={cn(
              "absolute right-2 top-1/2 -translate-y-1/2 z-10",
              "h-10 w-10 rounded-full bg-white/90 shadow-lg",
              "flex items-center justify-center",
              "hover:bg-white transition-colors",
              "disabled:opacity-40 disabled:cursor-not-allowed",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            )}
            aria-label="Next slide"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </>
      )}

      {/* Pagination Dots */}
      {showDots && childArray.length > slides && (
        <div className="flex items-center justify-center gap-2 mt-4" role="tablist" aria-label="Carousel pagination">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => scrollTo(index)}
              className={cn(
                "h-2 rounded-full transition-all",
                "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                selectedIndex === index
                  ? "w-8 bg-primary"
                  : "w-2 bg-neutral-300 hover:bg-neutral-400"
              )}
              aria-label={`Go to slide ${index + 1}`}
              aria-selected={selectedIndex === index}
              role="tab"
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Carousel item wrapper for better semantics (optional).
 */
export function CarouselItem({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={className}>{children}</div>;
}
